<template>
  <div class="home">
    <div class="a-filter dw-filters" :class="{ 'dw-filters--bare': !showFilters }">
      <div v-if="showFilters" class="dw-filters__title">
        <a-icon type="filter" /> Filters
      </div>
      <div class="dw-filters__row">
        <template v-if="showFilters">
          <div v-if="featureAccess && featureAccess['tags']" class="dw-filter-field">
            <label class="filter-label">Tags</label>
            <div class="full-w a-select-group" :class="{ active: (filterParams.tags || []).length > 0 }">
              <a-button size="large">
                <a-icon type="tag" />
              </a-button>
              <a-select
                v-model="filterParams.tags"
                mode="multiple"
                size="large"
                placeholder="Filter by tags"
              >
                <a-select-option v-for="tag in availableTags" :key="tag.id" :value="tag.id">
                  {{ tag.name }}
                </a-select-option>
              </a-select>
            </div>
          </div>
          <div v-if="availablePlanTypes.length" class="dw-filter-field">
            <label class="filter-label">Plan type</label>
            <div class="full-w a-select-group" :class="{ active: (filterParams.planTypes || []).length > 0 }">
              <a-button size="large">
                <a-icon type="crown" />
              </a-button>
              <a-select
                v-model="filterParams.planTypes"
                mode="multiple"
                size="large"
                placeholder="Filter by plan type"
              >
                <a-select-option v-for="plan in availablePlanTypes" :key="plan" :value="plan">
                  {{ plan | lowercase | capitalize }}
                </a-select-option>
              </a-select>
            </div>
          </div>
        </template>

        <!-- Docs + layout controls, right side of the filter row. -->
        <div class="dw-edit-bar">
          <a
            class="dw-docs"
            href="https://docs.weborion.io/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <a-icon type="book" class="dw-docs__icon" />
            <span class="dw-docs__title">WebOrion Docs</span>
            <span class="dw-docs__desc">Guides &amp; reference for using the portal</span>
            <a-icon type="arrow-up" class="dw-docs__arrow" />
          </a>
          <div class="dw-edit-bar__actions">
            <a-button v-if="!editMode" :loading="refreshing" :disabled="refreshCooldown" @click="refresh">
              <a-icon v-if="!refreshing" type="reload" /> Refresh
            </a-button>
            <template v-if="editMode">
              <a-button @click="addModalVisible = true">
                <a-icon type="plus" /> Add widget
              </a-button>
              <a-button @click="resetLayout">
                <a-icon type="reload" /> Reset
              </a-button>
              <a-button type="primary" @click="editMode = false">
                <a-icon type="check" /> Done
              </a-button>
            </template>
            <a-button v-else @click="editMode = true">
              <a-icon type="appstore" /> Edit layout
            </a-button>
          </div>
        </div>
      </div>
    </div>

    <DashboardGrid
      ref="grid"
      :editMode="editMode"
      :tagIds="appliedTags"
      :planTypes="appliedPlanTypes"
      @state="onGridState"
    />

    <a-modal
      v-model="addModalVisible"
      title="Add a widget"
      :footer="null"
      :width="560"
    >
      <div class="dw-add-list">
        <template v-for="section in addableSections">
          <div :key="section.group" class="dw-add-sep">{{ section.group }}</div>
          <button
            v-for="w in section.widgets"
            :key="w.i"
            type="button"
            class="dw-add-card"
            @click="addWidgetFromModal(w.i)"
          >
            <div class="dw-add-card__head">
              <span class="dw-add-card__title">
                <a-icon :type="w.icon" class="dw-add-card__icon" /> {{ w.title }}
              </span>
              <a-icon type="plus-circle" class="dw-add-card__add" />
            </div>
            <div class="dw-add-card__desc">
              {{ w.description }}
            </div>
          </button>
        </template>

        <p v-if="!gridState.addable.length" class="dw-add-empty">
          All available widgets are already on your dashboard.
        </p>

        <template v-if="lockedSections.length">
          <div class="dw-add-sep dw-add-sep--locked">Not included in your plan</div>
          <template v-for="section in lockedSections">
            <div :key="section.key" class="dw-add-locked">
              <div class="dw-add-locked__head">
                <a-icon type="lock" />
                <span class="dw-add-locked__name">{{ section.name }}</span>
              </div>
              <p class="dw-add-locked__reason">{{ section.reason }}</p>
              <div
                v-for="w in section.widgets"
                :key="w.i"
                class="dw-add-card dw-add-card--locked"
              >
                <div class="dw-add-card__head">
                  <span class="dw-add-card__title">
                    <a-icon :type="w.icon" class="dw-add-card__icon" /> {{ w.title }}
                  </span>
                  <span class="dw-menu-tag">{{ section.name }}</span>
                </div>
                <div class="dw-add-card__desc">
                  {{ w.description }}
                </div>
              </div>
            </div>
          </template>
          <p class="dw-add-support">
            To find out more about these features, contact
            <a :href="`mailto:${supportEmail}`">{{ supportEmail }}</a>.
          </p>
        </template>
      </div>
    </a-modal>
  </div>
</template>

<script>
  import { debounce } from 'lodash';
  import DashboardGrid from './components/dashboard/DashboardGrid.vue';
  import { GROUP_ORDER, FEATURES, SUPPORT_EMAIL } from './components/dashboard/widgetRegistry';
  import TagsService from '@modules/Tags/services/tags.service';
  import DataService from '@shared/services/data.service';

  const PLAN_TYPE_ORDER = ['GOLD', 'SILVER', 'BRONZE', 'TRIAL'];

  export default {
    name: 'Home',
    components: {
      DashboardGrid
    },
    data() {
      return {
        availableTags: [],
        availablePlanTypes: [],
        editMode: false,
        // Add/locked widget lists from DashboardGrid, for the Add-widget modal.
        gridState: { addable: [], locked: [] },
        filterParams: {
          tags: [],
          planTypes: []
        },
        // Debounced copy of filterParams handed to the widgets.
        appliedTags: [],
        appliedPlanTypes: [],
        refreshing: false,
        refreshCooldown: false,
        refreshCooldownTimer: null,
        addModalVisible: false
      };
    },
    watch: {
      'filterParams.tags': {
        handler() {
          this.applyFilters();
        },
        deep: true
      },
      'filterParams.planTypes': {
        handler() {
          this.applyFilters();
        },
        deep: true
      }
    },
    created() {
      // Long enough that a run of clicks through the multi-selects commits once.
      this.applyFilters = debounce(this.commitFilters, 700);
    },
    beforeDestroy() {
      if (this.applyFilters && this.applyFilters.cancel) this.applyFilters.cancel();
      if (this.refreshCooldownTimer) clearTimeout(this.refreshCooldownTimer);
    },
    computed: {
      featureAccess() {
        return this.$store.getters.featureAccess;
      },
      showFilters() {
        return (this.featureAccess && this.featureAccess['tags']) || this.availablePlanTypes.length > 0;
      },
      supportEmail() {
        return SUPPORT_EMAIL;
      },
      // Available widgets, split into the registry's sections (empty ones dropped).
      addableSections() {
        return GROUP_ORDER
          .map((group) => ({
            group,
            widgets: this.gridState.addable.filter((w) => w.group === group)
          }))
          .filter((s) => s.widgets.length);
      },
      // Locked widgets grouped by the feature that unlocks them, so the reason is
      // stated once per feature rather than repeated per widget.
      lockedSections() {
        const byFeature = new Map();
        this.gridState.locked.forEach((w) => {
          const key = w.requiresFeature;
          if (!byFeature.has(key)) {
            const feature = FEATURES[key] || {};
            byFeature.set(key, {
              key,
              name: feature.name || key,
              reason: feature.reason || 'This feature is not enabled on your account.',
              widgets: []
            });
          }
          byFeature.get(key).widgets.push(w);
        });
        return Array.from(byFeature.values());
      }
    },
    mounted() {
      if (this.featureAccess && this.featureAccess['tags']) {
        this.fetchAvailableTags();
      }
      this.fetchAvailablePlanTypes();
    },
    methods: {
      commitFilters() {
        this.appliedTags = Array.isArray(this.filterParams.tags) ? [...this.filterParams.tags] : [];
        this.appliedPlanTypes = Array.isArray(this.filterParams.planTypes)
          ? [...this.filterParams.planTypes]
          : [];
      },
      onGridState(state) {
        this.gridState = state;
      },
      async refresh() {
        if (this.refreshing || this.refreshCooldown || !this.$refs.grid) return;
        this.refreshing = true;
        try {
          await this.$refs.grid.refresh();
        } finally {
          this.refreshing = false;
          // Brief cooldown so repeat-clicks don't each trigger a full reload.
          this.refreshCooldown = true;
          this.refreshCooldownTimer = setTimeout(() => {
            this.refreshCooldown = false;
          }, 2500);
        }
      },
      // Kept open so several can be added; the item drops out as gridState updates.
      addWidgetFromModal(i) {
        if (this.$refs.grid) this.$refs.grid.addWidget(i);
      },
      resetLayout() {
        if (this.$refs.grid) this.$refs.grid.resetLayout();
      },
      async fetchAvailableTags() {
        try {
          const tagsData = await TagsService.getList((this.$store.state.user.data || {}).id);
          this.availableTags = Array.isArray(tagsData) ? tagsData : [];
        } catch (e) {
          this.availableTags = [];
        }
      },
      async fetchAvailablePlanTypes() {
        try {
          const userData = await DataService.getUsers();
          const usage = (userData && userData.plan_usage) || {};
          this.availablePlanTypes = PLAN_TYPE_ORDER.filter(
            (plan) => usage[plan] && Number(usage[plan].limit) > 0
          );
        } catch (e) {
          this.availablePlanTypes = [];
        }
      }
    }
  };
</script>
<style lang="scss" scoped>
  // Docs shortcut.
  .dw-docs {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
    font-family: Roboto;
    font-size: 12.5px;
    line-height: 20px;
    color: #858688;
    text-decoration: none;
    white-space: nowrap;
    transition: color 120ms ease;
    .dw-docs__icon {
      color: #20a4b8;
      font-size: 15px;
    }
    .dw-docs__title {
      color: #151b22;
      font-weight: 600;
    }
    .dw-docs__arrow {
      font-size: 12px;
      opacity: 0.7;
      transform: rotate(45deg);
    }
    &:hover {
      color: #20a4b8;
      .dw-docs__title {
        color: #20a4b8;
      }
    }
  }
  .dw-filters {
    display: block;
    height: auto;
    padding-bottom: 8px;
  }
  .dw-filters__title {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 12px;
    color: #151b22;
    font-family: Roboto;
    font-size: 15px;
    font-weight: 600;
    line-height: 20px;
    .anticon {
      color: #20a4b8;
    }
  }
  // Filter inputs + layout controls in one row; controls pushed right, bottom-aligned.
  .dw-filters__row {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 16px;
  }
  .dw-filter-field {
    flex: 1 1 220px;
    min-width: 0;
    max-width: 340px;
  }
  // Docs at label height, stacked above the action buttons.
  .dw-edit-bar {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    flex: none;
    margin-left: auto;
    margin-bottom: 0;
  }
  .dw-edit-bar__actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 8px;
  }
  // No filters: keep the bar slim.
  .dw-filters--bare {
    padding-top: 10px;
    padding-bottom: 10px;
  }
  .filter-label {
    display: inline-block;
    margin-bottom: 8px;
    color: #323232;
    font-family: Roboto;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
  }

  // ── Add-widget modal ──────────────────────────────────────────────────────
  .dw-add-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
    font-family: Roboto;
  }
  .dw-add-card {
    display: block;
    width: 100%;
    text-align: left;
    padding: 12px 14px;
    border: 1px solid #e8e8e8;
    border-radius: 6px;
    background: #ffffff;
    cursor: pointer;
    transition: border-color 120ms ease, box-shadow 120ms ease, background 120ms ease;
    &:hover {
      border-color: #20a4b8;
      background: #f7fdfe;
      box-shadow: 0 1px 4px rgba(32, 164, 184, 0.15);
    }
    &__head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
    }
    &__title {
      color: #151b22;
      font-size: 14px;
      font-weight: 600;
    }
    &__add {
      color: #20a4b8;
      font-size: 16px;
      flex: none;
    }
    &__desc {
      margin-top: 4px;
      color: #858688;
      font-size: 12.5px;
      line-height: 1.45;
    }
    &--locked {
      cursor: default;
      background: #fafafa;
      &:hover {
        border-color: #e8e8e8;
        background: #fafafa;
        box-shadow: none;
      }
      .dw-add-card__title { color: #858688; }
    }
  }
  .dw-add-sep {
    margin-top: 6px;
    color: #a9a9a9;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    &--locked {
      margin-top: 14px;
      padding-top: 12px;
      border-top: 1px solid #e8e8e8;
    }
  }
  .dw-add-card__icon {
    margin-right: 6px;
    color: #858688;
  }
  .dw-add-locked {
    border: 1px solid #e8e8e8;
    border-radius: 4px;
    padding: 10px 12px;
    background: #fafafa;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .dw-add-locked__head {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #151b22;
    font-weight: 600;
    font-size: 13px;
  }
  .dw-add-locked__reason {
    margin: 0;
    color: #858688;
    font-size: 12px;
    line-height: 1.5;
  }
  .dw-add-support {
    margin: 2px 0 0;
    color: #858688;
    font-size: 12px;
  }
  .dw-add-empty {
    margin: 4px 0;
    color: #858688;
    font-size: 13px;
  }
</style>
